import type { Translation } from '../../types';
import type { ops as en } from '../en/ops';

export const ops: Translation<typeof en> = {
  crumb: 'Vận hành',
  shared: {
    updated: 'Cập nhật lúc {{time}}',
    run: 'Chạy',
    runHint: 'Nhấn Ctrl+Enter để chạy truy vấn',
    reasonHint: 'Truy vấn này nêu tên một đối tượng nghiệp vụ; bạn sẽ phải nhập lý do.',
    open: 'Mở',
    none: 'Không có',
    rawResponse: 'Phản hồi gốc',
    withheld: '{{label}}: bị ẩn. Bạn chưa có quyền xem dữ liệu này.',
    empty: '{{label}}: không có.',
    loadFailed: 'Không tải được dữ liệu',
    notConfigured: 'Chưa được cấu hình ở môi trường này',
    showAll: 'Xem tất cả ({{count}})',
    showLess: 'Thu gọn',
    clearFilter: 'Bỏ lọc',
  },
  status: { ok: 'Ổn định', warn: 'Cảnh báo', crit: 'Nghiêm trọng', unknown: 'Không có dữ liệu' },
  severity: { P0: 'Nghiêm trọng', P1: 'Cao', P2: 'Trung bình', P3: 'Thông tin' },
  incidentStatus: { OPEN: 'Mới mở', ACKNOWLEDGED: 'Đã tiếp nhận', MITIGATING: 'Đang khắc phục', MONITORING: 'Đang theo dõi', RESOLVED: 'Đã xử lý' },
  workflowState: {
    PENDING: 'Chờ chạy',
    RUNNING: 'Đang chạy',
    WAITING: 'Đang chờ',
    RETRYING: 'Đang thử lại',
    COMPLETED: 'Hoàn tất',
    FAILED: 'Thất bại',
    MANUAL_REVIEW: 'Chờ xem xét thủ công',
    CANCELLED: 'Đã huỷ',
    COMPENSATING: 'Đang bù trừ',
  },
  stepOutcome: { STARTED: 'Đã bắt đầu', COMPLETED: 'Hoàn tất', FAILED: 'Thất bại', RETRY: 'Thử lại', WAITING: 'Đang chờ', UNKNOWN: 'Không xác định', SKIPPED: 'Bỏ qua' },
  commandState: {
    PENDING: 'Chờ gửi',
    SENT: 'Đã gửi',
    SUCCESS: 'Thành công',
    REJECTED: 'Bị từ chối',
    UNKNOWN: 'Không xác định',
    RECONCILING: 'Đang đối soát',
    RETRY_PENDING: 'Chờ thử lại',
    MANUAL_REVIEW: 'Chờ xem xét thủ công',
  },
  auditOutcome: { SUCCESS: 'Thành công', DENIED: 'Bị từ chối', FAILURE: 'Thất bại', ERROR: 'Lỗi' },
  eventStatus: { PUBLISHED: 'Đã phát', PENDING: 'Chờ phát', FAILED: 'Thất bại' },
  alertState: { active: 'Đang kích hoạt', suppressed: 'Đã tắt tiếng', unprocessed: 'Chưa xử lý' },
  gap: {
    MISSING_DOSSIER: 'Thiếu hồ sơ',
    INCOMPLETE_DOSSIER: 'Hồ sơ chưa đầy đủ',
    VERIFICATION_NOT_VALID: 'Xác minh không hợp lệ',
    SUBJECT_MISMATCH: 'Chủ thể không khớp',
    VNNIC_SYNC_MISSING: 'Chưa đồng bộ',
    VNNIC_SYNC_PENDING: 'Đang chờ đồng bộ',
    VNNIC_SYNC_REJECTED: 'Đồng bộ bị từ chối',
    VNNIC_SYNC_ERROR: 'Lỗi đồng bộ',
    REPORT_PROJECTION_MISSING: 'Thiếu dữ liệu báo cáo',
    VNNIC_REGISTRY_MISSING: 'Không có trong danh sách VNNIC',
  },

  overview: {
    title: 'Tổng quan vận hành',
    subtitle: 'Tình trạng registry, dòng tiền, tuân thủ và nền tảng trong một màn hình.',
    summary: {
      crit_one: '{{count}} nghiêm trọng',
      crit_other: '{{count}} nghiêm trọng',
      warn_one: '{{count}} cảnh báo',
      warn_other: '{{count}} cảnh báo',
      unknown_one: '{{count}} chưa có dữ liệu',
      unknown_other: '{{count}} chưa có dữ liệu',
      healthy: 'Mọi chỉ số đều ổn định',
      label: 'Tóm tắt sức khoẻ hệ thống',
    },
    updated: 'Cập nhật lúc {{time}}',
    updating: 'Đang cập nhật…',
    staleHint: 'Lần làm mới gần nhất bị lỗi; đang hiển thị dữ liệu trước đó.',
    autoRefresh: 'Tự làm mới 30 giây',
    autoRefreshHint: 'Tải lại sau mỗi 30 giây khi tab đang mở',
    attention: {
      title: 'Cần xử lý ngay',
      description: 'Mức nghiêm trọng cao nhất ở trên cùng. Mỗi mục dẫn tới nơi xử lý.',
      emptyTitle: 'Không có gì cần xử lý',
      emptyBody: 'Không có cảnh báo đang kích hoạt, sự cố đang mở, dịch vụ ngừng hoạt động, ngân sách lỗi cạn kiệt hay chỉ số vượt ngưỡng.',
      source: { alert: 'Cảnh báo', incident: 'Sự cố', indicator: 'Chỉ số', slo: 'SLO', service: 'Dịch vụ' },
      alertDetail: '{{name}} · bắt đầu {{ago}}',
      incidentDetail: '{{status}} · bắt đầu {{ago}}',
      serviceDown: '{{service}}: {{down}}/{{total}} instance không hoạt động',
      serviceDetail: 'Các instance này đã ngừng phản hồi kiểm tra sức khoẻ.',
      sloExhausted: '{{name}}: đã cạn ngân sách lỗi',
      sloLow: '{{name}}: còn {{remaining}} ngân sách lỗi',
      sloDetail: 'Mục tiêu {{objective}} · hiện tại {{current}} · cửa sổ {{window}}',
      act: 'Mở',
    },
    group: {
      registry: 'Registry & tên miền',
      registryHint: 'Lệnh gửi registry, đối soát và workflow cần người vận hành.',
      money: 'Tài chính',
      moneyHint: 'Bất biến sổ cái kép.',
      compliance: 'Tuân thủ',
      complianceHint: 'Mức bao phủ VNNIC của tên miền đang quản lý.',
      response: 'Ứng phó sự cố',
      responseHint: 'Cảnh báo đang kích hoạt và nhật ký sự cố.',
      platform: 'Nền tảng',
      platformHint: 'Dịch vụ, hàng đợi, outbox và mục tiêu SLO.',
    },
    tile: {
      ledger: {
        label: 'Toàn vẹn sổ cái',
        caption: 'Bút toán không cân (phải bằng 0)',
        alert: 'Bất biến bút toán kép bị vi phạm. Gọi trực ca billing; không tự hạch toán điều chỉnh.',
        link: 'Mở sổ cái',
      },
      unknown: {
        label: 'Kết quả registry chưa xác định',
        caption: 'UNKNOWN, đang đối soát hoặc chờ xem xét · tổng {{open}} giao dịch mở',
        alert: 'Xử lý bằng đối soát với trạng thái tại registry, không bao giờ thử lại mù quáng.',
        link: 'Sức khoẻ registry',
      },
      recon: {
        label: 'Tồn đọng đối soát',
        caption: 'Vấn đề đối soát đang mở',
        alert: 'Trạng thái tại registry và trong hệ thống đang lệch nhau.',
        link: 'Đối soát',
      },
      workflows: {
        label: 'Workflow chờ xem xét thủ công',
        caption: '{{inFlight}} workflow đang chạy',
        alert: 'Cần người vận hành ra quyết định.',
        link: 'Workflow',
      },
      queues: {
        label: 'Job sẵn sàng',
        caption: 'Job chờ lâu nhất từ {{age}} · {{dead}} job chết',
        captionIdle: 'Không có job chờ · {{dead}} job chết',
        alertDead_one: '{{count}} job đã hết số lần thử lại.',
        alertDead_other: '{{count}} job đã hết số lần thử lại.',
        alertOld: 'Job sẵn sàng lâu nhất đã chờ hơn 15 phút.',
        link: 'Hàng đợi',
      },
      outbox: {
        label: 'Outbox chờ phát',
        caption: 'Sự kiện chưa được phát',
        alert: 'Việc phát sự kiện đang bị chậm.',
        link: 'Outbox',
      },
      compliance: {
        label: 'Thiếu sót tuân thủ VNNIC',
        caption: 'Tên miền đang quản lý chưa được bao phủ đầy đủ',
        captionNone: 'Mọi tên miền đang quản lý đều được bao phủ',
        alert: 'Có tên miền thiếu hồ sơ, xác minh hoặc chưa đồng bộ VNNIC.',
        link: 'Mức bao phủ',
      },
      incidents: {
        label: 'Sự cố đang mở',
        caption: 'Chưa được xử lý xong',
        alert: 'Đang có sự cố.',
        link: 'Sự cố',
      },
      alerts: {
        label: 'Cảnh báo đang kích hoạt',
        caption: '{{silenced}} đã tắt tiếng hoặc bị ức chế',
        link: 'Cảnh báo',
      },
      services: {
        label: 'Instance dịch vụ hoạt động',
        caption: 'Đang theo dõi {{services}} dịch vụ',
        link: 'Dịch vụ',
      },
      slo: {
        label: 'SLO đạt mục tiêu',
        caption: '{{low}} SLO còn dưới 25% ngân sách lỗi',
        link: 'SLO',
      },
    },
    queueDepth: {
      title: 'Độ sâu hàng đợi job',
      description: 'Số job theo từng hàng đợi hiện tại; job đã lên lịch được tính riêng.',
      ready: 'Sẵn sàng',
      running: 'Đang chạy',
      retrying: 'Đang thử lại',
      dead: 'Chết',
      scheduled: '+{{count}} đã lên lịch',
      rowLabel: '{{queue}}: {{ready}} sẵn sàng, {{running}} đang chạy, {{retrying}} đang thử lại, {{dead}} chết',
      empty: 'Chưa có hàng đợi job nào',
    },
    inFlight: {
      title: 'Công việc đang xử lý',
      description: 'Giao dịch registry và workflow đang mở, theo trạng thái.',
      transactions: 'Giao dịch registry',
      workflows: 'Workflow',
      empty: 'Không có việc nào đang xử lý',
    },
    budgets: {
      title: 'Ngân sách lỗi còn lại',
      description: 'Thấp nhất ở trên, tính trên cửa sổ của từng SLO.',
      empty: 'Chưa định nghĩa SLO nào',
    },
    registries: {
      title: 'Registry',
      description: 'Công việc đang mở và kết quả lệnh gần nhất của từng registry.',
      open: '{{count}} đang mở',
      unknown: '{{count}} chưa xác định',
      lastSuccess: 'thành công gần nhất {{ago}}',
      never: 'chưa ghi nhận lần thành công nào',
      empty: 'Chưa cấu hình registry nào',
    },
    footnote: 'Đây là số liệu tại lần làm mới gần nhất, không phải dòng dữ liệu liên tục, và có thể chậm tới 15 giây. Xem xu hướng ở mục Metrics, mục tiêu ở mục SLO.',
    loadFailed: 'Không tải được trang tổng quan',
  },

  chart: {
    truncated: 'Đang hiển thị {{shown}}/{{total}} chuỗi; các chuỗi còn lại có trong bảng. Hãy gộp bằng sum by (…) để giảm số chuỗi.',
  },

  services: {
    title: 'Dịch vụ',
    subtitle: 'Toàn bộ dịch vụ của nền tảng và tình trạng phản hồi của từng instance.',
    filter: 'Dịch vụ',
    filterPlaceholder: 'Lọc theo tên…',
    onlyProblems: 'Chỉ dịch vụ có instance ngừng hoạt động',
    stat: { services: 'Dịch vụ', up: 'Instance hoạt động', down: 'Instance ngừng', allUp: 'Mọi instance đều phản hồi', downHint: 'Không phản hồi kiểm tra sức khoẻ' },
    col: { service: 'Dịch vụ', health: 'Tình trạng', instances: 'Instance' },
    health: '{{up}}/{{total}} hoạt động',
    down: 'ngừng',
    up: 'hoạt động',
    unlabelled: '(chưa có tên)',
    empty: 'Chưa có dịch vụ nào báo cáo',
    noMatch: 'Không có dịch vụ nào khớp bộ lọc',
    loadFailed: 'Không tải được danh sách dịch vụ',
    detail: {
      subtitle: 'Instance, những gì đang kích hoạt và các mục tiêu áp dụng cho dịch vụ này.',
      notFound: 'Không có dịch vụ nào tên {{name}} đang báo cáo',
      stat: { instances: 'Instance hoạt động', alerts: 'Cảnh báo đang kích hoạt', incidents: 'Sự cố đang mở' },
      instances: 'Instance',
      col: { instance: 'Instance', state: 'Trạng thái' },
      instancesEmpty: 'Chưa có instance nào báo cáo cho dịch vụ này',
      alerts: 'Cảnh báo của dịch vụ này',
      alertsEmpty: 'Không có cảnh báo nào đang kích hoạt cho dịch vụ này',
      incidents: 'Sự cố có liên quan tới dịch vụ này',
      incidentsEmpty: 'Chưa có sự cố nào liên quan tới dịch vụ này',
      slos: 'Mục tiêu áp dụng cho dịch vụ này',
      slosEmpty: 'Chưa có mục tiêu nào cho dịch vụ này',
      budgetLeft: 'còn {{value}} ngân sách lỗi',
      logs: 'Log',
      metrics: 'Lưu lượng yêu cầu',
      traces: 'Trace',
    },
  },

  registries: {
    title: 'Sức khoẻ registry',
    subtitle: 'Giao dịch registry đang mở và chưa xác định kết quả, theo từng registry.',
    badge: { ok: 'Ổn định', warn: 'Có vấn đề đang mở', crit: 'Có kết quả chưa xác định' },
    field: {
      open: 'Giao dịch đang mở',
      unknown: 'Chưa xác định / đang đối soát',
      issues: 'Vấn đề đang mở',
      lastSuccess: 'Thành công gần nhất',
      lastFailure: 'Lỗi gần nhất',
    },
    latest: 'mới nhất',
    eppLatency: 'Độ trễ EPP p95',
    logs: 'Log',
    traces: 'Trace',
    note: 'Thời gian registry phản hồi nằm ngoài tầm kiểm soát của chúng ta; phần thời gian do hệ thống của chúng ta thêm vào có mục tiêu riêng ở mục SLO.',
    empty: 'Chưa cấu hình registry nào',
    loadFailed: 'Không tải được danh sách registry',
  },

  queues: {
    title: 'Hàng đợi',
    subtitle: 'Hàng đợi job, outbox và workflow đang chạy.',
    stat: { ready: 'Job sẵn sàng', dead: 'Job chết', outbox: 'Outbox chờ phát', workflows: 'Workflow đang chạy', deadHint: 'Đã hết lượt thử lại; cần quyết định', readyHint: 'Đang chờ worker nhận' },
    jobs: { title: 'Tác vụ đã lên lịch (hàng đợi job)', empty: 'Không có hàng đợi nào' },
    outbox: { title: 'Outbox (sự kiện chờ phát)', empty: 'Không có sự kiện chờ' },
    workflows: { title: 'Workflow đang chạy', empty: 'Không có workflow nào đang chạy' },
    col: {
      queue: 'Hàng đợi',
      ready: 'Sẵn sàng',
      running: 'Đang chạy',
      scheduled: 'Đã lên lịch',
      retrying: 'Đang thử lại',
      dead: 'Chết',
      oldestReady: 'Sẵn sàng lâu nhất',
      maxAttempts: 'Số lần thử tối đa',
      eventType: 'Loại sự kiện',
      pending: 'Đang chờ',
      oldest: 'Cũ nhất',
      workflowType: 'Loại workflow',
      state: 'Trạng thái',
      count: 'Số lượng',
      oldestInState: 'Lâu nhất ở trạng thái',
    },
    loadFailed: 'Không tải được hàng đợi',
  },

  metrics: {
    title: 'Metrics',
    subtitle: 'Truy vấn số liệu của nền tảng. Mọi truy vấn đều được ghi vào nhật ký audit.',
    promql: 'Truy vấn',
    mode: 'Loại truy vấn',
    range: 'Khoảng thời gian',
    instant: 'Tức thời',
    step: 'Bước (giây)',
    stepAuto: 'tự động',
    instantHint: 'Truy vấn tức thời được tính tại thời điểm hiện tại, hoặc tại cuối khoảng tuỳ chọn.',
    examplesTitle: 'Ví dụ',
    example: {
      http5xx: 'Tỷ lệ HTTP 5xx theo dịch vụ',
      eppP95: 'Độ trễ EPP p95 theo registry',
      eppCommands: 'Lệnh EPP theo thao tác',
      workflows: 'Workflow theo trạng thái',
      outbox: 'Outbox chờ phát',
      ledger: 'Mất cân đối sổ cái',
    },
    emptyTitle: 'Nhập một truy vấn',
    emptyBody: 'Chọn một ví dụ ở trên hoặc tự viết. Khoảng thời gian tối đa 30 ngày và 11.000 điểm.',
    querying: 'Đang truy vấn…',
    failed: 'Truy vấn thất bại',
    emptyResult: 'Kết quả rỗng',
    resultTitle: 'Kết quả',
    col: { series: 'Chuỗi', value: 'Giá trị', min: 'Nhỏ nhất', max: 'Lớn nhất', last: 'Gần nhất' },
    tableView_one: 'Xem dạng bảng ({{count}} chuỗi)',
    tableView_other: 'Xem dạng bảng ({{count}} chuỗi)',
  },

  logs: {
    title: 'Log',
    subtitle: 'Tìm kiếm log hoặc theo dõi trực tiếp. Dữ liệu cá nhân đã được loại bỏ trước khi log tới màn hình này, và mọi lần tìm kiếm đều được ghi vào nhật ký audit.',
    logql: 'Truy vấn',
    liveTail: 'Theo dõi trực tiếp',
    limit: 'Giới hạn',
    builder: {
      title: 'Tạo truy vấn từ bộ lọc',
      service: 'Dịch vụ',
      level: 'Mức log',
      any: 'Bất kỳ',
      registry: 'Registry',
      operation: 'Thao tác',
      traceId: 'Trace ID',
      requestId: 'Mã yêu cầu',
      correlationId: 'Correlation ID',
      errorCode: 'Mã lỗi',
      text: 'Dòng chứa',
      use: 'Dùng truy vấn này',
    },
    row: { trace: 'Trace', correlation: 'Correlation', context: 'Ngữ cảnh ±1 phút', raw: 'Dòng gốc' },
    tail: {
      pause: 'Tạm dừng',
      resume: 'Tiếp tục',
      status_one: 'Kiểm tra mỗi {{interval}} giây cho {{window}} giây gần nhất · đang giữ {{count}} dòng (tối đa {{max}})',
      status_other: 'Kiểm tra mỗi {{interval}} giây cho {{window}} giây gần nhất · đang giữ {{count}} dòng (tối đa {{max}})',
      running: 'Đang chạy',
      paused: 'Đã tạm dừng',
      stopped: 'Đã dừng theo dõi trực tiếp',
      waiting: 'Đang chờ log…',
    },
    emptyTitle: 'Nhập một truy vấn',
    emptyBody: 'Bắt đầu với một stream selector như {{example}}, hoặc tạo từ bộ lọc.',
    searching: 'Đang tìm log…',
    failed: 'Truy vấn log thất bại',
    result_one: '{{count}} dòng, mới nhất trước',
    result_other: '{{count}} dòng, mới nhất trước',
    limitReached: 'đã chạm giới hạn {{limit}}',
    scrubbed: 'đã loại bỏ dữ liệu cá nhân',
    range: '{{start}} đến {{end}}',
    noLines: 'Không có dòng log nào trong khoảng này',
    resultTitle: 'Kết quả',
  },

  traces: {
    title: 'Trace',
    subtitle: 'Theo dấu một yêu cầu đi qua các dịch vụ. Chỉ tìm được theo các thuộc tính đã được phê duyệt.',
    jumpTitle: 'Mở trace theo ID',
    jump: 'Trace ID',
    jumpPlaceholder: '32 ký tự hex',
    searchTitle: 'Tìm theo thuộc tính',
    attribute: 'Thuộc tính {{n}}',
    value: 'Giá trị {{n}}',
    remove: 'Xoá',
    addAttribute: 'Thêm thuộc tính',
    search: 'Tìm kiếm',
    emptyTitle: 'Tìm theo thuộc tính',
    emptyBody: 'Ví dụ registry = VNNIC, hoặc một correlation ID.',
    searching: 'Đang tìm trace…',
    failed: 'Tìm trace thất bại',
    noTraces: 'Không có trace nào trong khoảng này',
    col: { trace: 'Trace', root: 'Span gốc', started: 'Bắt đầu', duration: 'Thời lượng' },
  },

  trace: {
    title: 'Trace',
    crumb: 'Trace',
    invalid: '{{id}} không phải trace ID. Trace ID gồm 32 ký tự hexa.',
    loading: 'Đang tải trace…',
    notFound: 'Không tìm thấy trace (có thể đã quá thời gian lưu giữ)',
    failed: 'Không tải được trace',
    noSpans: 'Phản hồi không có span nào',
    noSpansBody: 'Trace này trả về ở định dạng mà màn hình này chưa hỗ trợ.',
    field: { root: 'Gốc', started: 'Bắt đầu', duration: 'Thời lượng', spans: 'Số span', services: 'Dịch vụ', errors: 'Lỗi' },
    logsFor: 'Log của trace này',
    correlation: 'Correlation (sự kiện, workflow, registry)',
    tab: { waterfall: 'Biểu đồ span', raw: 'JSON gốc' },
    waterfall: {
      label: 'Các span',
      span: 'Span',
      timeline: 'Dòng thời gian ({{total}})',
      error: 'lỗi',
      spanId: 'Span ID',
      parent: 'Span cha',
      root: 'gốc',
      kind: 'Loại',
      startOffset: 'Độ lệch bắt đầu',
      duration: 'Thời lượng',
      status: 'Trạng thái',
    },
  },

  correlation: {
    title: 'Correlation',
    invalid: '{{id}} không phải correlation ID (UUID).',
    loading: 'Đang tổng hợp…',
    failed: 'Không tổng hợp được dữ liệu',
    follow: {
      logs: 'Mở trong trình xem log',
      traces: 'Tìm trace',
      history: 'Lịch sử {{domain}}',
    },
    emptyTitle: 'Không có bản ghi nào với correlation ID này',
    emptyBody: 'Không có sự kiện tên miền, workflow, giao dịch registry, bản ghi audit hay sự kiện đã phát nào mang ID này. Log và trace vẫn có thể có.',
    tab: {
      events: 'Sự kiện tên miền',
      workflows: 'Workflow',
      transactions: 'Giao dịch registry',
      audit: 'Audit',
      published: 'Sự kiện đã phát',
      logs: 'Log',
    },
    col: {
      when: 'Thời điểm',
      domain: 'Tên miền',
      sequence: '#',
      event: 'Sự kiện',
      request: 'Request',
      workflow: 'Workflow',
      state: 'Trạng thái',
      step: 'Bước',
      failure: 'Lỗi',
      resource: 'Tài nguyên',
      started: 'Bắt đầu',
      operation: 'Thao tác',
      trid: 'clTRID / svTRID',
      result: 'Kết quả',
      completed: 'Hoàn tất',
      action: 'Hành động',
      actor: 'Tác nhân',
      outcome: 'Kết quả',
      created: 'Tạo lúc',
      eventType: 'Loại sự kiện',
      status: 'Trạng thái',
    },
    noSvtrid: 'chưa có svTRID',
    logs: {
      loading: 'Đang đọc log…',
      failed: 'Không đọc được log của luồng này',
      summary_one: '{{count}} dòng, cũ nhất trước (đúng trình tự luồng chạy)',
      summary_other: '{{count}} dòng, cũ nhất trước (đúng trình tự luồng chạy)',
      window: 'Từ {{start}} đến {{end}}: từ bản ghi sớm nhất đến muộn nhất của luồng, thêm 15 phút mỗi phía.',
      fallback: 'Không có bản ghi để khoanh thời gian: tìm trong 7 ngày gần nhất, {{start}} đến {{end}}.',
      none: 'Không có dòng log nào mang correlation ID này trong khoảng thời gian trên.',
      older: 'Tải thêm log cũ hơn',
      capped: 'Mỗi lần tìm lấy {{limit}} dòng mới nhất; có thể còn dòng cũ hơn.',
      query: 'Truy vấn đã chạy:',
    },
    exchange: {
      title: 'Nội dung trao đổi',
      hint: 'yêu cầu và phản hồi đúng như đã gửi và nhận với registry; đọc từ kho log và giải mã khi mở, mỗi lần xem được ghi kiểm toán',
      group: 'Giao dịch registry',
      ungrouped: 'Trao đổi với registry (không gắn giao dịch)',
      noCltrid: 'Dòng log không mang clTRID đọc được nên không mở được nội dung trao đổi.',
    },
  },

  alerts: {
    title: 'Cảnh báo',
    subtitle: 'Những gì đang kích hoạt, theo nhóm, tạo sự cố chỉ với một cú nhấp.',
    groupBy: 'Nhóm theo',
    group: { severity: 'Mức độ', alertname: 'Cảnh báo', service: 'Dịch vụ' },
    showSilenced: 'Hiện cả cảnh báo đã tắt tiếng / bị ức chế',
    readOnly: 'Ở đây cảnh báo chỉ để xem: việc tắt tiếng và xác nhận cảnh báo thực hiện trong công cụ cảnh báo. Việc tạo sự cố từ cảnh báo thì làm ngay trên trang này.',
    stat: { firing: 'Đang kích hoạt', urgent: 'P0 / P1', silenced: 'Đã tắt tiếng / bị ức chế', urgentHint: 'Gọi người trực', silencedHint: 'Đã tắt tiếng nhưng điều kiện vẫn còn' },
    emptyTitle: 'Không có cảnh báo nào',
    emptyBody: 'Hiện không có cảnh báo nào đang kích hoạt.',
    since: 'bắt đầu {{ago}}',
    openIncident: 'Tạo sự cố',
    runbook: 'Runbook',
    runbookPath: 'Runbook:',
    loadFailed: 'Không tải được cảnh báo',
    detail: {
      subtitle: 'Mọi lần kích hoạt của cảnh báo này, runbook và các sự cố có liên quan.',
      firing: 'Đang kích hoạt',
      firingEmpty: 'Cảnh báo này hiện không kích hoạt',
      stat: { firing: 'Đang kích hoạt', silenced: 'Đã tắt tiếng / bị ức chế', severity: 'Mức độ' },
      services: 'Dịch vụ bị ảnh hưởng',
      incidents: 'Sự cố có liên quan tới cảnh báo này',
      incidentsEmpty: 'Chưa có sự cố nào liên quan tới cảnh báo này',
    },
  },

  incidents: {
    title: 'Sự cố',
    subtitle: 'Toàn bộ sự cố, chưa xử lý xong xếp trước. Mở và chuyển trạng thái sự cố cần quyền quản lý sự cố.',
    open: 'Tạo sự cố',
    filter: { status: 'Trạng thái', active: 'Chưa xử lý xong', resolved: 'Đã xử lý', all: 'Tất cả' },
    col: { severity: 'Mức', title: 'Tiêu đề', status: 'Trạng thái', owner: 'Người phụ trách', started: 'Bắt đầu' },
    empty: 'Chưa có sự cố nào',
    emptyFiltered: 'Không có sự cố nào ở trạng thái này',
    openHint: 'Mở một sự cố để xem diễn biến và chuyển bước tiếp theo.',
    loadFailed: 'Không tải được danh sách sự cố',
    form: {
      title: 'Tạo sự cố',
      description: 'Thời gian sự cố bắt đầu tính từ bây giờ. Hãy liên kết các cảnh báo và dịch vụ liên quan để người xử lý theo dõi.',
      severity: 'Mức độ',
      titleLabel: 'Tiêu đề',
      relatedAlerts: 'Cảnh báo liên quan',
      relatedServices: 'Dịch vụ liên quan',
      commaHint: 'Phân cách bằng dấu phẩy',
      opening: 'Đang tạo…',
      submit: 'Tạo sự cố',
    },
    move: {
      button: 'Chuyển trạng thái',
      title: 'Chuyển trạng thái sự cố',
      description: 'Sự cố chỉ đi tiếp về phía trước (từ đang theo dõi có thể quay lại đang khắc phục).',
      newStatus: 'Trạng thái mới',
      rootCause: 'Nguyên nhân gốc',
      resolution: 'Cách xử lý',
      recommended: 'nên điền',
      optional: 'không bắt buộc',
      requiredToResolve: 'bắt buộc khi đóng sự cố',
      saving: 'Đang lưu…',
      submit: 'Chuyển sang {{status}}',
      closed: 'Sự cố này đã được xử lý; không thể chuyển tiếp nữa.',
    },
    detail: {
      tabOverview: 'Tổng quan',
      tabAnalysis: 'Nguyên nhân & cách xử lý',
      subtitle: 'Bắt đầu {{ago}}',
      status: 'Trạng thái',
      started: 'Bắt đầu',
      acknowledged: 'Tiếp nhận',
      resolved: 'Xử lý xong',
      duration: 'Thời lượng',
      owner: 'Người phụ trách',
      id: 'Mã sự cố',
      relatedAlerts: 'Cảnh báo liên quan',
      relatedServices: 'Dịch vụ liên quan',
      rootCause: 'Nguyên nhân gốc',
      resolution: 'Cách xử lý',
      notRecorded: 'Chưa ghi nhận',
      notFound: 'Không tìm thấy sự cố',
      loadFailed: 'Không tải được sự cố',
      timeline: 'Diễn biến',
      timelineEmpty: 'Chưa ghi nhận thay đổi nào',
      opened: 'Đã mở sự cố',
      movedTo: 'Chuyển sang {{status}}',
      by: 'bởi {{who}}',
      reason: 'Lý do',
      alertsEmpty: 'Chưa liên kết cảnh báo nào với sự cố này',
      servicesEmpty: 'Chưa liên kết dịch vụ nào với sự cố này',
      domainsNote: 'Sự cố ghi nhận các dịch vụ bị ảnh hưởng chứ không ghi từng tên miền. Hãy đi từ một dịch vụ bị ảnh hưởng sang log và trace của dịch vụ đó để tìm các tên miền đứng sau.',
    },
  },

  slo: {
    title: 'Mục tiêu chất lượng dịch vụ (SLO)',
    subtitle: 'Mức cam kết của từng dịch vụ, mức đang đạt được và ngân sách lỗi còn lại.',
    stat: { total: 'SLO', meeting: 'Đạt mục tiêu', low: 'Ngân sách ≤ 25%', exhausted: 'Cạn ngân sách', lowHint: 'Ưu tiên công việc về độ tin cậy', exhaustedHint: 'Đóng băng phát hành tính năng' },
    col: { slo: 'SLO', service: 'Dịch vụ', objective: 'Mục tiêu', current: 'Hiện tại', budget: 'Ngân sách lỗi còn lại', window: 'Cửa sổ' },
    queryFailed: 'không đo được',
    noBackend: 'chưa đo ở môi trường này',
    exhausted: 'Đã cạn',
    meterLabel: 'Ngân sách lỗi còn lại của {{name}}',
    policyTitle: 'Chính sách ngân sách lỗi',
    policy: 'Khi còn 25% hoặc ít hơn, ưu tiên công việc về độ tin cậy; khi đã cạn, đóng băng phát hành tính năng của dịch vụ đó. Số liệu hiện tại được đo trên cửa sổ SLO mỗi lần mở trang này.',
    empty: 'Chưa định nghĩa SLO nào',
    loadFailed: 'Không tải được SLO',
  },

  workflows: {
    title: 'Workflow',
    subtitle: 'Các tác vụ dài hạn và tiến độ của từng tác vụ, mới nhất trước.',
    filter: {
      status: 'Trạng thái',
      any: 'Bất kỳ',
      active: 'Đang hoạt động (chờ / chạy / đợi / thử lại)',
      type: 'Loại',
      resource: 'Tài nguyên',
      correlation: 'Correlation',
    },
    col: {
      workflow: 'Workflow',
      status: 'Trạng thái',
      step: 'Bước hiện tại',
      resource: 'Tài nguyên',
      attempt: 'Lần thử',
      failure: 'Lỗi',
      created: 'Tạo lúc',
      updated: 'Cập nhật',
      correlation: 'Correlation',
    },
    leased: 'đang giữ',
    leasedBy: 'Đang được {{worker}} giữ',
    empty: 'Không có workflow nào khớp',
    loadFailed: 'Không tải được danh sách workflow',
  },

  workflow: {
    title: 'Workflow',
    crumb: 'Workflow',
    notFound: 'Không tìm thấy workflow',
    loadFailed: 'Không tải được workflow',
    tab: { instance: 'Thông tin', steps: 'Các bước ({{count}})', commands: 'Lệnh registry ({{count}})', payload: 'Đầu vào và ngữ cảnh' },
    instanceTitle: 'Thông tin workflow',
    field: {
      state: 'Trạng thái',
      step: 'Bước hiện tại',
      businessKey: 'Khoá nghiệp vụ',
      attempt: 'Lần thử',
      resource: 'Tài nguyên',
      nextRun: 'Lần chạy tiếp theo',
      lease: 'Worker đang giữ',
      leaseValue: '{{worker}} đến {{until}}',
      leaseNone: 'chưa có worker nào giữ',
      failure: 'Lỗi',
      created: 'Tạo lúc',
      updated: 'Cập nhật',
      completed: 'Hoàn tất',
      version: 'Phiên bản',
    },
    follow: {
      title: 'Truy vết',
      correlation: 'Correlation',
      request: 'Request',
      causedBy: 'Sự kiện khởi phát',
      logs: 'Log của thao tác này',
      traces: 'Trace của thao tác này',
    },
    steps: {
      empty: 'Chưa có bước nào chạy',
      attempt: 'lần thử {{n}}',
      started: 'bắt đầu {{at}}',
      worker: 'worker',
      trace: 'trace',
      output: 'Kết quả (đã che dữ liệu)',
    },
    commands: { empty: 'Workflow này chưa gửi lệnh nào tới registry' },
    payload: { note: 'Bí mật được loại bỏ và định danh cá nhân được che trước khi dữ liệu rời máy chủ.', input: 'Đầu vào', context: 'Ngữ cảnh' },
  },

  retention: {
    title: 'Lưu trữ log',
    subtitle: 'Mỗi loại log được giữ trong kho log bao nhiêu ngày trước khi bị xoá.',
    note: 'Log của mọi dịch vụ, worker, máy chủ web, cơ sở dữ liệu và hàng đợi tin nhắn đều vào kho log. Trao đổi với registry và nhà đăng ký (yêu cầu và phản hồi EPP, REST) được giữ riêng, theo số ngày riêng. Hồ sơ nghiệp vụ (lệnh registry, nhật ký thao tác) nằm trong cơ sở dữ liệu và không bị ảnh hưởng. Log quá hạn được dọn mỗi giờ; kho log hoàn tất việc xoá trong vòng một ngày.',
    section: 'Số ngày lưu',
    registry_exchange_days: { label: 'Trao đổi với registry và nhà đăng ký', hint: 'Yêu cầu và phản hồi EPP, REST của mọi lệnh, đọc hay ghi. Từ {{min}} đến {{max}} ngày.' },
    other_days: { label: 'Mọi log khác', hint: 'Dịch vụ, worker, máy chủ web, proxy, cơ sở dữ liệu, hàng đợi tin nhắn. Từ {{min}} đến {{max}} ngày.' },
    reason: 'Lý do thay đổi',
    save: 'Lưu',
    readOnly: 'Chỉ quản trị vận hành được thay đổi.',
    updated: 'Thay đổi lần cuối {{at}}',
    defaults: 'Giá trị mặc định, chưa ai thay đổi.',
  },
};
